import type {
    MultisigPreflightResponse,
    SquadsMultisigStatus,
    SquadsPreflightFetch
} from "@shared/yaas/squadsMultisig";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import { API_URLS } from "@solomon-apps/shared";

import { isRecord } from "./verificationResponse";

const isOptionalString = (value: unknown): value is string | undefined =>
    value === undefined || typeof value === "string";
const isOptionalCount = (value: unknown): value is number | undefined =>
    value === undefined || (typeof value === "number" && Number.isSafeInteger(value) && value >= 0);

function isPreflightResponse(value: unknown): value is MultisigPreflightResponse {
    return (
        isRecord(value) &&
        ["ok", "not-found", "lookup-failed", "vault-unknown-to-system", "unsupported-version", "resolving"].some(
            (status) => status === value.status
        ) &&
        isOptionalString(value.multisigAddress) &&
        isOptionalString(value.programId) &&
        isOptionalString(value.version) &&
        (value.vaultAddress === null || isOptionalString(value.vaultAddress)) &&
        isOptionalCount(value.memberCount) &&
        isOptionalCount(value.threshold) &&
        (value.resolvedFrom === undefined || value.resolvedFrom === "multisig" || value.resolvedFrom === "vault") &&
        (value.signerStatus === undefined ||
            ["can-verify", "cannot-verify", "not-member", "no-signer"].some(
                (status) => status === value.signerStatus
            )) &&
        (value.members === undefined ||
            (Array.isArray(value.members) &&
                value.members.every(
                    (member: unknown) =>
                        isRecord(member) && typeof member.address === "string" && typeof member.canVerify === "boolean"
                )))
    );
}

/** Shared transport for account selection and enrollment's disabled-feature guard. */
export const preflightRetailSquadsMultisig: SquadsPreflightFetch = async ({ address, signerAddress, signal }) => {
    const response = await fetch(`${API_URLS.YAAS}/api/yaas/multisig-preflight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, ...(signerAddress ? { signerAddress } : {}) }),
        signal
    });
    if (!response.ok) throw new Error("Couldn't check this wallet. Please try again.");
    const value: unknown = await response.json();
    if (!isPreflightResponse(value)) throw new Error("Invalid Squads response");
    return value;
};

/** Recheck disabled-feature requests, including restored micro-transfer retries. */
export async function assertRetailSquadsAvailable(
    address: string,
    signerAddress: string,
    signal?: AbortSignal,
    enabled = FEATURE_FLAGS.retailSquadsMultisig
): Promise<void> {
    if (enabled) return;
    const result = await preflightRetailSquadsMultisig({ address, signerAddress, signal });
    if (result.status !== "not-found") {
        throw new Error("Squads wallets are temporarily unavailable. Use an ordinary wallet destination.");
    }
}

export interface SquadsDestinationEligibility {
    canSubmit: boolean;
    message: string | null;
    isError: boolean;
    canRetry: boolean;
}

export function squadsDestinationStatus(
    status: SquadsMultisigStatus,
    enabled = FEATURE_FLAGS.retailSquadsMultisig
): SquadsDestinationEligibility {
    if (!enabled && ["success", "vault-unknown-to-system", "unsupported-version"].includes(status.status)) {
        return {
            canSubmit: false,
            message: "Squads wallets are temporarily unavailable",
            isError: true,
            canRetry: false
        };
    }
    switch (status.status) {
        case "not-found":
            return { canSubmit: true, message: null, isError: false, canRetry: false };
        case "success": {
            const canSubmit = status.signerStatus === "can-verify";
            const message = canSubmit
                ? // Mirrors the field's own "Valid account" verdict — the
                  // multisig's shape moves to the badge beside it.
                  "Valid signer"
                : status.signerStatus === "no-signer"
                  ? "Connect a wallet with Vote or Execute permission"
                  : // Not a member and a member without Vote/Execute read the
                    // same to the user: the wallet lacks the permissions.
                    "Missing permissions";
            return { canSubmit, message, isError: !canSubmit, canRetry: false };
        }
        case "idle":
        case "loading":
            return { canSubmit: false, message: "Checking permissions...", isError: false, canRetry: false };
        case "invalid":
            return { canSubmit: false, message: null, isError: true, canRetry: false };
        case "lookup-failed":
            // Deliberately not `status.error`: that is the raw transport
            // failure ("Failed to fetch (yaas-dev.solomonlabs.io)"), which
            // told the user nothing and leaked the host into the form.
            return { canSubmit: false, message: "Check failed", isError: true, canRetry: true };
        case "vault-unknown-to-system":
            return {
                canSubmit: false,
                message: "Check failed",
                isError: true,
                canRetry: true
            };
        case "unsupported-version":
            return { canSubmit: false, message: "Must be Squads V4 or newer", isError: true, canRetry: false };
    }
}
